/** Public secure-share landing for ONE Document Vault links.
 *  Token-gated, no auth. Enforces expiry / password / download limits
 *  server-side; this page only orchestrates the authorised fetch. */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import { FolderLock, Download, Lock, ShieldCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function VaultSharePublic() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch(`${API}/vault/s/${token}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json()).detail || "Link unavailable"); return r.json(); })
      .then(setMeta)
      .catch((e) => setError(e.message));
  }, [token]);

  const download = async () => {
    setBusy(true); setError(null);
    try {
      const url = `${API}/vault/s/${token}/file${password ? `?password=${encodeURIComponent(password)}` : ""}`;
      const r = await fetch(url);
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || (r.status === 401 ? "Password incorrect" : "Download failed")); }
      const blob = await r.blob();
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = r.headers.get("content-disposition") || "";
      const fn = /filename="?([^"]+)"?/.exec(cd)?.[1] || "document";
      a.href = dl; a.download = fn; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(dl), 30000);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="vault-public-share">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-sm p-8">
        <div className="flex items-center gap-2 text-slate-400 text-[11px] uppercase tracking-[0.16em]">
          <FolderLock className="h-4 w-4" /> SUNTRONIX ONE · Secure Share
        </div>
        {error && !meta ? (
          <div className="mt-6 text-center text-rose-600" data-testid="vault-public-error">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
            {error}
          </div>
        ) : !meta ? (
          <div className="mt-6 text-slate-400 text-sm">Loading…</div>
        ) : (
          <>
            <h1 className="mt-3 text-xl font-bold text-slate-900">{meta.doc_name}</h1>
            <p className="text-[12px] text-slate-500 mt-1 flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              Shared securely. Expires {String(meta.expiry).slice(0, 16).replace("T", " ")}.
            </p>
            {meta.password_protected && (
              <div className="mt-4">
                <div className="text-[11px] font-medium text-slate-500 mb-1 flex items-center gap-1"><Lock className="h-3.5 w-3.5" />Password required</div>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="vault-public-password" />
              </div>
            )}
            {error && <div className="mt-3 text-[12px] text-rose-600" data-testid="vault-public-error">{error}</div>}
            {meta.download_allowed ? (
              <Button className="mt-5 w-full bg-slate-900 text-white hover:bg-slate-800" onClick={download} disabled={busy} data-testid="vault-public-download">
                <Download className="h-4 w-4 mr-1" /> {busy ? "Preparing…" : "Download document"}
              </Button>
            ) : (
              <div className="mt-5 text-center text-[12px] text-slate-400">Download is not permitted for this link.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
